import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FieldHelperProps, FieldInputProps, FieldMetaProps, useField } from 'formik';
import _ from 'lodash';
import { Alert, FieldControl, Label, Select, SelectOption } from '@americanexpress/dls-react';
import { IconCheck } from '@americanexpress/dls-icons';

import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import styles from './selectDetailed.module.scss';
import { trackClick } from 'src/hooks/useAdobeTags';
import { TransactionFrequencyTooltip } from 'src/components/atomic/organisms/transaction/frequencyTooltip/TransactionFrequencyTooltip';
import FormatService from 'src/services/format.service';
import { useLocation } from 'react-router-dom';
import adobeTags from 'src/types/adobe-tags';
import LiteralsService from 'src/services/literals/LiteralsService';
import Selectors from 'src/utils/selectors';
import useFilteredOptions from 'src/hooks/useFilteredOptions';
import Anchor from 'src/components/atomic/atoms/anchor/Anchor';
import { MenuSearch } from 'dls-react-filters';
import { Key } from 'src/types/keys.enum';
import { isKeyboardKey, isOneOfKeyboardKeys } from 'src/utils/key.utils';
import { useSelectDetailedContext } from 'src/hooks/useSelectDetailedContext';

type SelectDetailedProps = {
    id: string;
    label: string;
    hideLabel?: boolean;
    isForFrequency?: boolean;
    placeholder?: string;
    twoLined?: boolean;
    floatLeft?: boolean;
    className?: string;
    frequencyTooltipClassName?: string;
    onChange?: (...a: any[]) => void | Promise<void>;
    customTrackClick?: (e: any) => void;
    options: any[];
    valueKey?: string;
    labelKey?: string;
    iconKey?: string;
    anchorOnClickKey?: string;
    tagName?: string;
    disableAlert?: boolean;
    isSelectable?: (e?: any) => boolean;
} & (
    | {
          nested?: false;
          weak?: boolean;
          isNested?: undefined;
      }
    | {
          nested: true;
          weak?: undefined;
          isNested?: (e: any) => boolean;
      }
);

export default function SelectDetailed({
    id,
    label,
    hideLabel = false,
    isForFrequency = false,
    placeholder,
    twoLined = false,
    floatLeft = false,
    className,
    onChange,
    customTrackClick,
    options,
    valueKey = 'id',
    labelKey = 'name',
    iconKey = 'icon',
    anchorOnClickKey = 'onClick',
    weak = false,
    nested = false,
    isNested = (e) => e.parentId || e.onClick || e.href,
    isSelectable = (e) => !e.onClick && !e.href,
    tagName,
    frequencyTooltipClassName,
    disableAlert = false
}: SelectDetailedProps): JSX.Element {
    const fieldControlRef = useRef<HTMLDivElement>(null);
    const selectRef = useRef<HTMLElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const dropdownBoxRef = useRef<HTMLDivElement>(null);
    const { activeSelectDetailedId, changeActiveSelectDetailedId, uniqueId, preventClose } = useSelectDetailedContext();
    const { pathname } = useLocation();
    const [field, meta, helpers]: [FieldInputProps<string>, FieldMetaProps<string>, FieldHelperProps<string>] =
        useField(id);
    const { setValue } = helpers;
    const { value } = meta;

    const matchingOptionIndex = useMemo(
        () => Math.max(_.findIndex(options, { [valueKey]: value }), 0),
        [options, value, valueKey]
    );

    const [open, setOpen] = useState<boolean>(false);
    const [focusedIndex, setFocusedIndex] = useState<number>();
    const { filteredOptions, handleFilter, searchProps, filterValue } = useFilteredOptions({
        options,
        labelKey,
        isChild: isNested,
        anchorOnClickKey,
        onSearchChange: () => listRef.current?.scrollTo(0, 0)
    });
    /** Keep filteredOptions and focus reactive to prop and context changes */
    useLayoutEffect(() => {
        handleFilter();
        if (open) {
            handleFocus(matchingOptionIndex, nested);
            if (nested) focusOnSearchInput();
        }
    }, [value, options, open, matchingOptionIndex, nested]);

    /** Close dropdown when clicking outside of list */
    useLayoutEffect(() => {
        document.addEventListener('click', handleClickOutside, !open || !preventClose);
        return (): void => document.removeEventListener('click', handleClickOutside, !open || !preventClose);
    }, [open, preventClose]);

    /** Focus on select element when it's the active one */
    useEffect(() => {
        if (activeSelectDetailedId === uniqueId && activeSelectDetailedId !== '' && !open) {
            selectRef.current?.focus();
        }
    }, [activeSelectDetailedId, open]);

    /** Handle Adobe tracking when option is clicked */
    const handleTrackClick = (focusedOption: any): void => {
        if (customTrackClick) customTrackClick(focusedOption);
        else if (tagName) {
            const formattedOption: string = FormatService.formatTag(focusedOption.name);
            const fullTagName: string = tagName + '>' + formattedOption;
            trackClick(pathname, fullTagName);
        }
    };

    /** Handle focus on option or search */
    const handleFocus = (index: number, scroll: boolean = false): void => {
        setFocusedIndex(index);
        if (index === -1) focusOnSearchInput();
        else {
            const option: HTMLElement = listRef.current?.children[index] as HTMLElement;
            // We need animation frame to prevent animation cancelling from search focus
            requestAnimationFrame(() => {
                if (scroll) option?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
                else {
                    // @ts-expect-error
                    // Types for scrollIntoView don't include 'instant' despite being supported (tested Chrome & Safari)
                    // In any case not critical if it isn't supported
                    option?.scrollIntoView({ block: 'nearest', behavior: focusedIndex === -1 ? 'instant' : 'smooth' });
                    option?.focus({ preventScroll: true });
                }
            });
        }
    };
    //
    /** Handle click outside of dropdown */
    const handleClickOutside = (e: MouseEvent): void => {
        if (fieldControlRef.current && !fieldControlRef.current.contains(e.target as Node) && open && !preventClose) {
            closeMenu();
        }
    };

    /** Attempts to update option if it's selectable, returns true when updated and false otherwise */
    const updateOption = (focusedOption: any): boolean => {
        let updated = false;
        if (isSelectable(focusedOption)) {
            setValue(focusedOption[valueKey]);
            void onChange?.();
            updated = true;
            handleTrackClick(focusedOption);
        }
        closeMenu(updated);
        return updated;
    };

    /** Method to open menu and handle side effects */
    const handleOpen = (e: any): void => {
        e.preventDefault();
        changeActiveSelectDetailedId(uniqueId);
        setOpen(true);
    };

    /** Method to close menu and handle side effects */
    const closeMenu = (selectedValue: boolean = false): void => {
        setOpen(false);
        if (!value && !selectedValue) helpers.setValue('', true);
    };

    /** Focus on search input field */
    const focusOnSearchInput = (): void => {
        setFocusedIndex(-1);
        searchProps.ref?.current?.input?.focus();
    };

    /** Key down for select element */
    const onKeyDownSelect = useCallback((e: React.KeyboardEvent<HTMLUListElement>): void => {
        // open dropdown
        if (isOneOfKeyboardKeys(e, [Key.SPACE, Key.ENTER, Key.ARROW_DOWN, Key.ARROW_UP])) {
            handleOpen(e);
        }
    }, []);

    /** Key down for list element */
    const onKeyDownList = (e: React.KeyboardEvent<HTMLUListElement>): void => {
        // focus previous
        if (isKeyboardKey(e, Key.ARROW_UP)) {
            e.preventDefault();
            const nextIndex = Math.max(focusedIndex - 1, nested ? -1 : 0);
            handleFocus(nextIndex);
        }

        // focus next
        else if (isKeyboardKey(e, Key.ARROW_DOWN)) {
            e.preventDefault();
            const nextIndex = Math.min(focusedIndex + 1, filteredOptions.length - 1);
            handleFocus(nextIndex);
        }

        // close without selection
        else if (isKeyboardKey(e, Key.ESCAPE) || (isKeyboardKey(e, Key.TAB) && !e.shiftKey)) {
            e.preventDefault();
            closeMenu();
        }

        // focus on search if present else close
        else if (isKeyboardKey(e, Key.TAB) && e.shiftKey) {
            e.preventDefault();
            if (nested) {
                setFocusedIndex(-1);
                searchProps.ref?.current?.focus();
            } else {
                closeMenu();
            }
        }

        // select value and close
        else if (isOneOfKeyboardKeys(e, [Key.ENTER, Key.SPACE])) {
            e.preventDefault();
            const focusedOption = filteredOptions[focusedIndex];
            if (!updateOption(focusedOption)) focusedOption[anchorOnClickKey]?.();
        }
    };

    /** Key down for search input */
    const onKeyDownSearchInput = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        // focus first option
        if (isKeyboardKey(e, Key.ARROW_DOWN)) {
            e.preventDefault();
            handleFocus(0);
        }

        // close menu
        if (
            (isKeyboardKey(e, Key.TAB) && !searchProps.ref?.current?.clearButton) ||
            (isKeyboardKey(e, Key.TAB) && e.shiftKey) ||
            (isKeyboardKey(e, Key.ESCAPE) && !filterValue)
        ) {
            e.preventDefault();
            closeMenu();
        }

        // do nothing when enter is pressed
        if (isKeyboardKey(e, Key.ENTER)) {
            e.preventDefault(); // prevent form submission
        }
    };

    const selectedOption = _.find(filteredOptions, { [valueKey]: value }) || {
        [valueKey]: null,
        [labelKey]: placeholder
    };
    const hasError: boolean = meta.touched && meta.error?.length > 0;
    const alertId: string = `${id}Alert`; // DLS requires we associate Alert with Select in this particular way
    let currentHeadingLabel: string; // Keep tracking of heading label as looping through options

    return (
        <div ref={fieldControlRef} className={className} id={uniqueId}>
            <FieldControl
                status={hasError ? 'error' : 'default'}
                data-cy={`${id}Dropdown`}
                className={floatLeft ? styles.floatLeft : ''}
            >
                {!hideLabel && (
                    <div className={styles.label}>
                        <Label htmlFor={id} id={`budgetSelectLabel-${id}`}>
                            {label}
                        </Label>
                        {isForFrequency && (
                            <TransactionFrequencyTooltip
                                className={frequencyTooltipClassName}
                                onClick={() => trackClick(pathname, adobeTags.Tooltip.Frequency)}
                            />
                        )}
                    </div>
                )}
                <Select
                    theme={hideLabel ? { marginTop: 0 } : {}}
                    ref={selectRef}
                    id={id}
                    twoLined={twoLined}
                    onMouseDown={handleOpen}
                    onKeyDown={onKeyDownSelect}
                    fluid
                    // ada
                    data-cy={`${id}${Selectors.SpendSmart.SELECT_DETAILED}`}
                    aria-label={hideLabel ? label : undefined}
                    aria-expanded={open}
                    aria-haspopup="listbox"
                    tabIndex={0}
                    aria-describedby={hasError ? alertId : undefined}
                    {...field}
                    // Custom onBlur to prevent formik blur running when clicking on select detailed to open menu
                    onBlur={(e: any): void => {
                        if (!open) field.onBlur(e);
                    }}
                >
                    <SelectOption value={selectedOption[valueKey]} hidden>
                        {isForFrequency ? selectedOption[labelKey]?.split(' ')[0] : selectedOption[labelKey]}
                    </SelectOption>
                </Select>
                {open && (
                    <div ref={dropdownBoxRef} className={styles.dropdownBox}>
                        {nested && (
                            <MenuSearch
                                id={`${id}-menuSearch`}
                                focusOnSearchInput={focusOnSearchInput}
                                onClose={closeMenu}
                                onKeyDown={onKeyDownSearchInput}
                                data-cy={Selectors.SpendSmart.SELECT_DETAILED_SEARCH}
                                {...searchProps}
                            />
                        )}
                        {filteredOptions.length ? (
                            <ul
                                ref={listRef}
                                id={`budgetSelectDropdownOptions-${id}`}
                                style={{ visibility: open ? 'unset' : 'hidden' }}
                                className={styles.optionsContainer}
                                // ada
                                tabIndex={0}
                                onKeyDown={onKeyDownList}
                                role="listbox"
                                aria-label="Select options"
                                aria-labelledby={`budgetSelectLabel-${id}`}
                                data-cy={Selectors.SpendSmart.SELECT_DETAILED_OPTIONS}
                            >
                                {filteredOptions.map((e, i) => {
                                    const optionValue = e[valueKey];
                                    const optionLabel = e[labelKey];
                                    const OptionIcon = e[iconKey];
                                    const optionAnchorOnClick = e[anchorOnClickKey];

                                    const optionIsSelected = optionValue === selectedOption[valueKey];
                                    const optionIsNested = nested && isNested(e);
                                    const optionIsHeading = nested && !isNested(e);
                                    if (optionIsHeading) currentHeadingLabel = optionLabel;
                                    const ariaLabel = optionIsNested
                                        ? LiteralsService.getLiteral('General.dropdown.nestedOption', [
                                              optionLabel,
                                              currentHeadingLabel
                                          ])
                                        : optionIsHeading
                                        ? LiteralsService.getLiteral('General.dropdown.nestedHeading', [optionLabel])
                                        : undefined;

                                    if (optionAnchorOnClick)
                                        return (
                                            <Anchor
                                                key={`budgets-select-option-${i}`}
                                                className={`${nested ? styles.nestedAnchor : ''} ${styles.option}`}
                                                onClick={optionAnchorOnClick}
                                                data-cy={Selectors.SpendSmart.SELECT_OPTION}
                                                role="option"
                                                aria-selected={optionIsSelected ? 'true' : undefined}
                                                tabIndex={-1}
                                                aria-label={`${optionLabel} ${
                                                    currentHeadingLabel ? `within ${currentHeadingLabel}` : ''
                                                }`}
                                            >
                                                {optionLabel}
                                            </Anchor>
                                        );

                                    return (
                                        <li
                                            key={`budgets-select-option-${i}`}
                                            className={`${styles.option} ${optionIsSelected ? styles.selected : ''} ${
                                                focusedIndex === i ? styles.focused : ''
                                            }`}
                                            onClick={() => updateOption(filteredOptions[i])}
                                            // ada
                                            data-cy={Selectors.SpendSmart.SELECT_OPTION}
                                            role="option"
                                            aria-selected={optionIsSelected ? 'true' : undefined}
                                            aria-label={ariaLabel}
                                            tabIndex={0}
                                        >
                                            {OptionIcon && (
                                                <OptionIcon
                                                    data-cy={Selectors.SpendSmart.SELECT_OPTION_ICON}
                                                    className={styles.categoryIcon}
                                                    alt=""
                                                />
                                            )}
                                            {optionIsNested && <div className={styles.nestedIconPlaceholder}></div>}
                                            <Typography
                                                data-cy={Selectors.SpendSmart.SELECT_OPTION_TEXT}
                                                variant={
                                                    TypographyVariants[optionIsNested || weak ? 'BODY' : 'BODY_BOLD']
                                                }
                                            >
                                                {optionLabel}
                                            </Typography>
                                            {optionIsSelected && <IconCheck className={styles.checkIcon} />}
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : (
                            <div className={styles.optionsContainer}>
                                <div className={styles.disabledOption}>
                                    {LiteralsService.getLiteral('General.search.noResultsFor', [filterValue])}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {hasError && !disableAlert && (
                    <Alert id={alertId} data-cy={`${id}${Selectors.SpendSmart.SELECT_DETAILED_ERROR}`}>
                        {meta.error}
                    </Alert>
                )}
            </FieldControl>
        </div>
    );
}
